import { useCallback, useMemo } from 'react';
import Kanban from 'components/DesignSystems/Kanban';
import type {
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
} from 'components/DesignSystems/Kanban';
import { Card, CardContent } from '@/components/ui/card';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  mapResourceSetToEditPayload,
  type ResourceSet,
  useUpdateResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import {
  CHECK_IN_STATUS_COLUMNS,
  effectiveCheckInStatus,
  formatCheckInStatus,
  isAssignedToIncident,
} from './checkInStatus';
import { displayOrNull } from './dhsResourceDisplay';
import type { ResourceRow } from './ResourceTabCard';
import {
  AssetMapSymbolCheckbox,
  type AssetMapSymbolControls,
} from './AssetMapSymbolCheckbox';

const UNSET_COLUMN_ID = '__unset__';

export function AssetsCheckInBoard({
  rows,
  resourceBySetId,
  onSelectRow,
  mapSymbols,
}: {
  rows: ResourceRow[];
  resourceBySetId: Map<string, ResourceSet>;
  onSelectRow: (row: ResourceRow) => void;
  mapSymbols?: AssetMapSymbolControls;
}) {
  const { mutate: updateResourceSet } = useUpdateResourceSet('');

  const rowsById = useMemo(() => {
    const map = new Map<string, ResourceRow>();
    for (const row of rows) {
      map.set(row.id, row);
    }
    return map;
  }, [rows]);

  const board: KanbanBoard = useMemo(() => {
    const grouped = new Map<string, ResourceRow[]>();
    for (const column of CHECK_IN_STATUS_COLUMNS) {
      grouped.set(column, []);
    }
    grouped.set(UNSET_COLUMN_ID, []);

    for (const row of rows) {
      const effective = effectiveCheckInStatus(
        row.status,
        isAssignedToIncident(row)
      );
      const key =
        effective &&
        CHECK_IN_STATUS_COLUMNS.includes(
          effective as (typeof CHECK_IN_STATUS_COLUMNS)[number]
        )
          ? effective
          : UNSET_COLUMN_ID;
      const bucket = grouped.get(key) ?? grouped.get(UNSET_COLUMN_ID);
      bucket?.push(row);
    }

    const columns: KanbanColumn[] = [
      ...CHECK_IN_STATUS_COLUMNS.map((status) => {
        const cards = grouped.get(status) ?? [];
        return {
          id: status,
          title: `${status} (${cards.length})`,
          cards: cards.map(rowToCard),
        };
      }),
      {
        id: UNSET_COLUMN_ID,
        title: `Unset (${grouped.get(UNSET_COLUMN_ID)?.length ?? 0})`,
        cards: (grouped.get(UNSET_COLUMN_ID) ?? []).map(rowToCard),
      },
    ];

    return { columns };
  }, [rows]);

  const handleCardMove = useCallback(
    (card: KanbanCard, _source: KanbanColumn, destination: KanbanColumn) => {
      const row = rowsById.get(card.id);
      const resourceSet = row?.resource_set_id
        ? resourceBySetId.get(row.resource_set_id)
        : undefined;
      if (!row?.resource_set_id || !resourceSet) return;
      const assigned =
        isAssignedToIncident(row) || isAssignedToIncident(resourceSet);
      if (!assigned && destination.id !== UNSET_COLUMN_ID) return;
      const nextStatus =
        destination.id === UNSET_COLUMN_ID ? '' : destination.id;
      if ((row.status || '') === nextStatus) return;
      updateResourceSet({
        ...mapResourceSetToEditPayload(row.resource_set_id, resourceSet),
        status: nextStatus,
      });
    },
    [resourceBySetId, rowsById, updateResourceSet]
  );

  const renderCard = useCallback(
    (card: KanbanCard) => {
      const row = card.metadata as ResourceRow | undefined;
      if (!row) return null;
      return (
        <Card size="sm" className="tw:cursor-pointer tw:gap-1 tw:py-2">
          <CardContent className="tw:space-y-1 tw:px-3">
            <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-foreground">
              {mapSymbols ? (
                <AssetMapSymbolCheckbox row={row} controls={mapSymbols} />
              ) : null}
              <span className="tw:min-w-0 tw:truncate">
                {row.name || NULL_VALUE}
              </span>
            </div>
            <div className="tw:text-xs tw:text-muted-foreground">
              {displayOrNull(row.kind)}
            </div>
            <div className="tw:text-xs tw:text-muted-foreground">
              {formatCheckInStatus(row.status, isAssignedToIncident(row))}
            </div>
          </CardContent>
        </Card>
      );
    },
    [mapSymbols]
  );

  return (
    <Kanban
      board={board}
      onCardMove={handleCardMove}
      onCardClick={(card) => {
        const row = card.metadata as ResourceRow | undefined;
        if (row) onSelectRow(row);
      }}
      renderCard={renderCard}
    />
  );
}

function rowToCard(row: ResourceRow): KanbanCard {
  return {
    id: row.id,
    title: row.name,
    metadata: row,
  };
}
